import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { usePaginatedFetch } from '../shared/usePaginatedFetch';

const SchoolContext = createContext();

export const useSchool = () => useContext(SchoolContext);

export const SchoolProvider = ({ children }) => {
    const [selectedBranch, setSelectedBranch] = useState('All Branches');

    // State for Static Entities (Tier A)
    const [packages, setPackages] = useState([]);
    const [branches, setBranches] = useState([]);

    const [loadingStatic, setLoadingStatic] = useState(true);
    const [error, setError] = useState(null);

    const config = window.reactPress || window.rpVars || {};
    const API_BASE = (config.api?.rest_url || '/wp-json').replace(/\/$/, '') + '/sdt/v1';
    const nonce = config.api?.nonce || config.nonce;

    // Diagnostic logging
    if (!nonce) {
        console.warn('⚠️ WordPress nonce is missing! API authentication will fail.');
        console.log('Available config:', config);
    }

    const headers = {
        'Content-Type': 'application/json',
        ...(nonce && { 'X-WP-Nonce': nonce }) // Only add nonce if it exists
    };

    // Paginated State (Tier B) - 100-200 limits for BO charts
    const leadsData = usePaginatedFetch({ url: `${API_BASE}/leads`, headers, perPage: 100 });
    const studentsData = usePaginatedFetch({ url: `${API_BASE}/customer`, headers, perPage: 200 });
    const paymentsData = usePaginatedFetch({ url: `${API_BASE}/payments`, headers, perPage: 100 });
    const expensesData = usePaginatedFetch({ url: `${API_BASE}/expenses`, headers, perPage: 100 });
    const lessonsData = usePaginatedFetch({ url: `${API_BASE}/lessons`, headers, perPage: 100 });

    const fetchStaticData = useCallback(async () => {
        setLoadingStatic(true);
        try {
            const [pkgsRes, branchesRes] = await Promise.all([
                fetch(`${API_BASE}/packages`, { headers }),
                fetch(`${API_BASE}/branches`, { headers })
            ]);

            if (pkgsRes.ok) {
                const pkgData = await pkgsRes.json();
                setPackages(Array.isArray(pkgData) ? pkgData : []);
            }
            if (branchesRes.ok) {
                const bData = await branchesRes.json();
                if (Array.isArray(bData)) {
                    setBranches(bData.map(b => b.branch_name || b.name || b));
                }
            }
        } catch (err) {
            console.error('Error fetching Owner static data:', err);
            setError('Failed to load static live data.');
        } finally {
            setLoadingStatic(false);
        }
    }, [nonce]); // nonce is stable enough, but headers depends on it

    const fetchData = () => {
        fetchStaticData();
        leadsData.refresh();
        studentsData.refresh();
        paymentsData.refresh();
        expensesData.refresh();
        lessonsData.refresh();
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loading = loadingStatic || studentsData.loading || leadsData.loading;
    const students = studentsData.data;
    const leads = leadsData.data;
    const payments = paymentsData.data;
    const expenses = expensesData.data;
    const lessons = lessonsData.data;

    // --- Computed Data ---

    // Filtering
    const filteredStudents = selectedBranch === 'All Branches'
        ? students
        : students.filter(s => s.branch === selectedBranch);

    const filteredLeads = selectedBranch === 'All Branches'
        ? leads
        : leads.filter(l => l.branch === selectedBranch);

    // KPIs
    const totalStudents = filteredStudents.length;
    const activePackages = filteredStudents.filter(s => ['active', 'Active'].includes(s.status)).length;

    // Financials
    const filteredPayments = selectedBranch === 'All Branches'
        ? payments
        : payments.filter(p => p.branch === selectedBranch);

    const monthlyIncome = filteredPayments.reduce((acc, curr) => acc + parseFloat(curr.amount || 0), 0);
    const runwayMonths = 4.5;

    // Charts
    const packageDistribution = packages.map(pkg => ({
        name: pkg.name,
        value: filteredStudents.filter(s => s.package_id === pkg.id || s.packageName === pkg.name).length
    }));

    const statusDistribution = ['Active', 'Watchlist', 'Inactive'].map(status => ({
        name: status,
        value: filteredStudents.filter(s => (s.status || '').toLowerCase() === status.toLowerCase()).length
    }));

    const stages = ['New', 'Provisional', 'Simulator', 'Road Lesson', 'Graduated'];
    const stageDistribution = stages.map(stage => ({
        name: stage,
        value: filteredStudents.filter(s => (s.stage || '').toLowerCase() === stage.toLowerCase()).length
    }));

    // Generate Financial Data for Chart
    // Generate Financial Data for Chart
    const generateFinancialChartData = useCallback(() => {
        const today = new Date();
        const allMonths = [];

        // Generate label for last 6 months + next 6 months
        for (let i = -6; i < 6; i++) {
            const d = new Date(today.getFullYear(), today.getMonth() + i, 1);
            allMonths.push(d);
        }

        // Helper to sum by month
        const sumByMonth = (items, dateField, amountField, targetDate) => {
            return items.filter(item => {
                const itemDate = new Date(item[dateField]);
                return itemDate.getMonth() === targetDate.getMonth() &&
                    itemDate.getFullYear() === targetDate.getFullYear();
            }).reduce((sum, item) => sum + parseFloat(item[amountField] || 0), 0);
        };

        // 5-Day Average Calculation
        const fiveDaysAgo = new Date();
        fiveDaysAgo.setDate(today.getDate() - 5);

        const recentIncome = payments.filter(p => new Date(p.payment_date) >= fiveDaysAgo);
        const recentExpenses = expenses.filter(e => new Date(e.expense_date) >= fiveDaysAgo);

        const avgDailyIncome = recentIncome.reduce((s, p) => s + parseFloat(p.amount || 0), 0) / 5;
        const avgDailyExpense = recentExpenses.reduce((s, e) => s + parseFloat(e.amount || 0), 0) / 5;

        return allMonths.map(date => {
            const isFuture = date > today;
            const monthLabel = date.toLocaleString('default', { month: 'short' });

            let income = 0;
            let expenditure = 0;
            let projection = null;

            if (isFuture) {
                // Projection based on 5-day average * 30 days
                projection = (avgDailyIncome * 30) - (avgDailyExpense * 30);
                // Keep projection positive visually or handle logic as needed. 
                // Alternatively, user might want Projected Income vs Projected Expense.
                // Based on graph, "Projection" seems to be a single line. Assuming it's Net Projection or Income.
                // Let's assume Projected Income for the graph line if it's "Revenue" focused, or Net.
                // Looking at standard charts, usually it's projecting the trend.
                // Let's project Income for now as that's usually the "Growth" metric.
                projection = avgDailyIncome * 30;
            } else {
                income = sumByMonth(payments, 'payment_date', 'amount', date);
                expenditure = sumByMonth(expenses, 'expense_date', 'amount', date);
            }

            return {
                month: monthLabel,
                income: isFuture ? null : income,
                expenditure: isFuture ? null : expenditure,
                projection: isFuture ? projection : null
            };
        });
    }, [payments, expenses]);

    // Loss making logic
    const lossMakingStudents = filteredStudents.filter(s => s.is_loss_making || s.isLossMaking);

    // Branch Performance
    const branchPerformance = branches.map(bName => {
        const bStudents = students.filter(s => s.branch === bName);
        const bRevenue = payments.filter(p => p.branch === bName).reduce((acc, curr) => acc + parseFloat(curr.amount || 0), 0);
        return {
            branch: bName,
            students: bStudents.length,
            revenue: bRevenue,
            status: bRevenue > 5000 ? 'Healthy' : 'Stable'
        };
    });

    return (
        <SchoolContext.Provider value={{
            loading, error,
            students: studentsData.data,
            leads: leadsData.data,
            packages, branches,
            payments: paymentsData.data, expenses: expensesData.data, lessons: lessonsData.data,
            selectedBranch, setSelectedBranch,

            // Pagination exports
            loadMoreStudents: studentsData.loadMore,
            hasMoreStudents: studentsData.hasMore,
            totalStudentsCount: studentsData.total,

            loadMoreLeads: leadsData.loadMore,
            hasMoreLeads: leadsData.hasMore,
            totalLeadsCount: leadsData.total,

            loadMoreLessons: lessonsData.loadMore,
            hasMoreLessons: lessonsData.hasMore,
            totalLessonsCount: lessonsData.total,

            loadMorePayments: paymentsData.loadMore,
            hasMorePayments: paymentsData.hasMore,

            loadMoreExpenses: expensesData.loadMore,
            hasMoreExpenses: expensesData.hasMore,
            totalStudents,
            activePackages,
            monthlyIncome,
            runwayMonths,
            packageDistribution,
            statusDistribution,
            stageDistribution,
            financialData: generateFinancialChartData(),
            branchPerformance,
            lossMakingStudents,
            packages,
            financials: { payments, expenses },
            lessons,
            refetch: fetchData
        }}>
            {children}
        </SchoolContext.Provider>
    );
};
